export interface DonorNameMatchRange {
  start: number;
  end: number;
}

const MIN_TOKEN_LENGTH = 4;

const NAME_STOPWORDS = [
  'the',
  'and',
  'of',
  'inc',
  'llc',
  'llp',
  'ltd',
  'co',
  'corp',
  'corporation',
  'company',
  'foundation',
  'fund',
  'funds',
  'trust',
  'charity',
  'charities',
  'charitable',
  'family',
  'group',
  'holdings',
  'partners',
  'association',
  'organization',
  'institute',
  'society',
];

const BOUNDARY_CHARS = '\\w\'\u2019';
const POSSESSIVE_SUFFIX = '(?:[\'\u2019]s|s[\'\u2019])?';

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const toWords = (value: string): string[] =>
  value
    .replace(/[.,]/g, ' ')
    .split(/\s+/)
    .map(word => word.trim())
    .filter(Boolean);

export const getDonorNameVariants = (
  donorName: string | null | undefined,
): string[] => {
  if (!donorName) return [];
  const words = toWords(donorName);
  if (!words.length) return [];
  const fullName = words.join(' ');
  const tokens = words.filter(
    word =>
      word.length >= MIN_TOKEN_LENGTH &&
      !NAME_STOPWORDS.includes(word.toLowerCase()),
  );
  const variants = [fullName, ...tokens].filter(
    variant => variant.length >= MIN_TOKEN_LENGTH,
  );
  return Array.from(new Set(variants));
};

const buildVariantRegExp = (variant: string): RegExp =>
  new RegExp(
    `(^|[^${BOUNDARY_CHARS}])(${escapeRegExp(variant).replace(
      /\s+/g,
      '\\s+',
    )}${POSSESSIVE_SUFFIX})(?![${BOUNDARY_CHARS}])`,
    'gi',
  );

const mergeRanges = (ranges: DonorNameMatchRange[]): DonorNameMatchRange[] => {
  const sorted = [...ranges].sort((a, b) => a.start - b.start);
  return sorted.reduce<DonorNameMatchRange[]>((merged, range) => {
    const previous = merged[merged.length - 1];
    if (previous && range.start <= previous.end) {
      previous.end = Math.max(previous.end, range.end);
      return merged;
    }
    merged.push({ ...range });
    return merged;
  }, []);
};

export const findDonorNameMatches = (
  text: string | null | undefined,
  donorName: string | null | undefined,
): DonorNameMatchRange[] => {
  if (!text) return [];
  const variants = getDonorNameVariants(donorName);
  if (!variants.length) return [];
  const ranges: DonorNameMatchRange[] = [];
  variants.forEach(variant => {
    const regExp = buildVariantRegExp(variant);
    let match = regExp.exec(text);
    while (match !== null) {
      const start = match.index + match[1].length;
      ranges.push({ start, end: start + match[2].length });
      regExp.lastIndex = start + match[2].length;
      match = regExp.exec(text);
    }
  });
  return mergeRanges(ranges);
};

const CN_DONOR_NAME = 'True Impact SuperAdmin';
export const hasDonorName = (
  text: string | null | undefined,
  donorName: string | null | undefined,
): boolean => {
  if (donorName === CN_DONOR_NAME) return false;
  return findDonorNameMatches(text, donorName).length > 0;
};
